import { liveQuery } from "dexie";

import type { LocalCartDraft } from "./cart-state";
import { posRuntimeDb } from "./pos-db";
import type { TrustedTerminalIdentity } from "./terminal-state";

export type CurrentCartRuntimeSnapshot = {
  scopeKey: string | null;
  cart: LocalCartDraft | null;
  revision: number;
};

export type CurrentCartRuntimeToken = {
  scopeKey: string;
  revision: number;
};

let state: CurrentCartRuntimeSnapshot = {
  scopeKey: null,
  cart: null,
  revision: 0,
};

const listeners = new Set<() => void>();
let cartJournalSubscription: { unsubscribe(): void } | null = null;

export function currentCartRuntimeScopeKey(identity: TrustedTerminalIdentity) {
  return `org:${identity.organization_id}:branch:${identity.branch_id}:node:${identity.node_id}`;
}

function emit() {
  for (const listener of listeners) listener();
}

function stopWatchingCartJournal() {
  cartJournalSubscription?.unsubscribe();
  cartJournalSubscription = null;
}

function watchCartJournal(identity: TrustedTerminalIdentity, cart: LocalCartDraft | null) {
  stopWatchingCartJournal();
  if (!cart) return;

  const expectedScopeKey = currentCartRuntimeScopeKey(identity);
  const expectedCartId = cart.localCartId;
  let observedDurableCart = false;

  cartJournalSubscription = liveQuery(() => posRuntimeDb.journal.get(expectedCartId)).subscribe({
    next: (record) => {
      if (record) {
        observedDurableCart = true;
        return;
      }
      if (!observedDurableCart) return;
      if (state.scopeKey !== expectedScopeKey || state.cart?.localCartId !== expectedCartId) return;

      // Sale Local Commit atomically deletes the source CART journal only after the
      // immutable SALE_ORDER + payment fact + durable sync request are persisted.
      // Preserve the trusted scope while publishing an authoritative empty cart so
      // consumers cannot fall back to a stale pre-commit in-memory draft.
      clearCurrentCartRuntime(identity);
    },
    error: () => {
      // The durable journal remains authoritative. A transient local observer error
      // must never recreate, delete or mutate a transaction.
    },
  });
}

export function readCurrentCartRuntime() {
  return state;
}

export function captureCurrentCartRuntimeToken(identity: TrustedTerminalIdentity): CurrentCartRuntimeToken {
  return {
    scopeKey: currentCartRuntimeScopeKey(identity),
    revision: state.revision,
  };
}

export function isCurrentCartRuntimeTokenCurrent(
  identity: TrustedTerminalIdentity,
  token: CurrentCartRuntimeToken,
) {
  return token.scopeKey === currentCartRuntimeScopeKey(identity) && state.revision === token.revision;
}

export function subscribeCurrentCartRuntime(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function publishCurrentCartRuntime(identity: TrustedTerminalIdentity, cart: LocalCartDraft | null) {
  const scopeKey = currentCartRuntimeScopeKey(identity);
  if (state.scopeKey === scopeKey && state.cart === cart) return state;

  state = {
    scopeKey,
    cart,
    revision: state.revision + 1,
  };
  watchCartJournal(identity, cart);
  emit();
  return state;
}

export function publishCurrentCartRuntimeIfCurrent(
  identity: TrustedTerminalIdentity,
  cart: LocalCartDraft | null,
  token: CurrentCartRuntimeToken,
) {
  if (!isCurrentCartRuntimeTokenCurrent(identity, token)) return false;
  publishCurrentCartRuntime(identity, cart);
  return true;
}

export function clearCurrentCartRuntime(identity?: TrustedTerminalIdentity) {
  const nextScopeKey = identity ? currentCartRuntimeScopeKey(identity) : null;
  if (identity && state.scopeKey !== nextScopeKey) return state;
  if (state.scopeKey === nextScopeKey && state.cart === null) return state;

  stopWatchingCartJournal();
  state = {
    scopeKey: nextScopeKey,
    cart: null,
    revision: state.revision + 1,
  };
  emit();
  return state;
}
